import type { Component } from 'vue'
import type { RouteRecordRaw } from 'vue-router'
import type { SettingsRole } from '@/composables/useViewAsRole'

/**
 * ─────────────────────────────────────────────────────────────────────────
 * Settings dashboard — single source of truth (FLY3D-914)
 *
 * This array drives BOTH the sidebar navigation (grouped) AND the nested
 * child routes under `/settings`. To add a new section or subsection,
 * add an entry here — the sidebar and routing pick it up automatically.
 *
 * Each subsection lazy-loads its own panel component, so screens can be
 * filled in one-by-one across the phased build (see FLY3D-914 checklist).
 * ─────────────────────────────────────────────────────────────────────────
 */

export interface SettingsSubsection {
  /** Display title in the sidebar + screen header. */
  title: string
  /** Path segment under `/settings` (also used to build the route name). */
  route: string
  /** Icon key — see SettingsNavIcon.vue. */
  icon: string
  /**
   * Lazy-loaded panel component rendered in the layout's <router-view>.
   * Optional for `chromeless` items, which navigate to their own top-level
   * route (registered in router/index.ts) instead of a /settings child.
   */
  component?: () => Promise<Component>
  /**
   * When true the nav item links to a top-level *chromeless* route (Public PIN,
   * Access Revoked) that renders outside the dashboard shell. Such items are
   * listed in the sidebar but excluded from `buildSettingsChildRoutes()`; their
   * route is registered manually with the matching `settingsRouteName(route)`.
   */
  chromeless?: boolean
  /** Eyebrow label shown above the screen title (usually the API endpoint). */
  endpoint?: string
  /** Short description rendered under the screen title. */
  description?: string
  /** Roles allowed to open this screen. Omit = all roles. */
  roles?: SettingsRole[]
  /** Small pill on the nav item, e.g. 'Soon' | 'Demo'. */
  badge?: string
  /** When true the topbar shows a "Coming soon" pill and the screen is planned. */
  planned?: boolean
  /** Build phase (used by the placeholder messaging). */
  phase?: number
}

export interface SettingsSection {
  title: string
  items: SettingsSubsection[]
}

// Role groupings (the real Cognito roles).
const MANAGER_PLUS: SettingsRole[] = ['org-manager', 'pilot-manager']
const OPERATOR_ONLY: SettingsRole[] = ['org-manager']
// Policies are fixed to the two manager roles (per the access decisions meeting).
const POLICY_ROLES: SettingsRole[] = ['org-manager', 'pilot-manager']
// "Everyone but viewer" — viewer links (FE hardcode; backend also blocks viewer create/list).
const NON_VIEWER: SettingsRole[] = ['org-manager', 'pilot-manager', 'pilot']

export const SETTINGS_SECTIONS: SettingsSection[] = [
  {
    title: 'Account',
    items: [
      {
        title: 'Profile',
        route: 'profile',
        icon: 'profile',
        endpoint: 'Account · GET /me',
        description: 'Your IDI-FLY identity, role binding, and organisation.',
        component: () => import('./sections/account/ProfilePanel.vue'),
      },
      {
        title: 'Notifications',
        route: 'notifications',
        icon: 'notifications',
        endpoint: 'Account · /me/notifications',
        description: 'Choose how IDI-FLY reaches you.',
        component: () => import('./sections/account/NotificationsPanel.vue'),
      },
      {
        title: 'Security',
        route: 'security',
        icon: 'security',
        endpoint: 'Account · /me/security',
        description: 'Multi-factor authentication and password.',
        component: () => import('./sections/account/SecurityPanel.vue'),
      },
    ],
  },
  {
    title: 'Organisation',
    items: [
      {
        title: 'Organisation',
        route: 'organisation',
        icon: 'organisation',
        endpoint: 'Organisation · org-wide configuration',
        description: "Your organisation's name and media retention policy.",
        // View open to all; edit is gated inside OrgSettings.vue (org-manager only, interim).
        component: () => import('./sections/organisation/OrgSettings.vue'),
      },
    ],
  },
  {
    title: 'Sharing',
    items: [
      {
        title: 'Shared With Me',
        route: 'shared-with-me',
        icon: 'shared',
        endpoint: 'Live · GET /sharing/shared-with-me',
        description: 'Resources you can reach directly or via a group, role, or share-group.',
        component: () => import('./sections/sharing/SharedWithMe.vue'),
        phase: 2,
      },
      {
        title: 'Share Dialog',
        route: 'share-dialog',
        icon: 'share',
        endpoint: 'Live · POST /sharing/share',
        description: 'Share a resource with people, groups, links or bundles.',
        roles: MANAGER_PLUS,
        component: () => import('./sections/sharing/ShareDialog.vue'),
        phase: 3,
      },
      {
        title: 'Access Requests',
        route: 'access-requests',
        icon: 'requests',
        endpoint: 'Live · /sharing/requests',
        description: "Request access you lack, or resolve others' requests.",
        component: () => import('./sections/sharing/AccessRequests.vue'),
        phase: 4,
      },
    ],
  },
  {
    title: 'Manage',
    items: [
      {
        title: 'Groups',
        route: 'groups',
        icon: 'groups',
        endpoint: 'Live · /sharing/groups',
        description: 'User bundles for granting access to a set of people at once.',
        roles: MANAGER_PLUS,
        component: () => import('./sections/manage/Groups.vue'),
        phase: 5,
      },
      {
        title: 'Share Groups',
        route: 'share-groups',
        icon: 'shareGroups',
        endpoint: 'Live · /sharing/share-groups',
        description: 'Bundle resources across types and share the whole set in one grant.',
        roles: MANAGER_PLUS,
        component: () => import('./sections/manage/ShareGroups.vue'),
        phase: 6,
      },
      {
        title: 'Viewer Links',
        route: 'viewer-links',
        icon: 'viewerLinks',
        endpoint: 'Live · /sharing/viewer-links',
        description: 'PIN-protected anonymous links to resources or bundles.',
        roles: NON_VIEWER,
        component: () => import('./sections/manage/ViewerLinks.vue'),
        phase: 7,
      },
      {
        title: 'API Keys',
        route: 'api-keys',
        icon: 'apiKeys',
        endpoint: 'Live · /sharing/api-keys',
        description: 'Programmatic keys scoped to capabilities. Secrets are shown once at mint.',
        // Available to everyone (not a resource); backend enforces any per-key limits.
        component: () => import('./sections/manage/ApiKeys.vue'),
        phase: 8,
      },
    ],
  },
  {
    title: 'Operator',
    items: [
      {
        title: 'Roles',
        route: 'roles',
        icon: 'roles',
        endpoint: 'Live · /sharing/roles · operator-only',
        description: 'Built-in roles map to Cognito groups. Custom roles are editable.',
        roles: OPERATOR_ONLY,
        component: () => import('./sections/operator/Roles.vue'),
        phase: 10,
      },
      {
        title: 'Policies',
        route: 'policies',
        icon: 'policies',
        endpoint: 'Live · /sharing/policies',
        description: 'Org-wide rules evaluated in the decision chain. Toggle to enable.',
        roles: POLICY_ROLES,
        component: () => import('./sections/operator/Policies.vue'),
        phase: 9,
      },
      {
        title: 'Audit Log',
        route: 'audit-log',
        icon: 'audit',
        endpoint: 'Live · /sharing/audit-log · operator-only',
        description: 'Per-resource history of grants, revocations and changes — append-only.',
        roles: OPERATOR_ONLY,
        component: () => import('./sections/operator/AuditLog.vue'),
        phase: 11,
      },
    ],
  },
]

/** The first subsection — the default landing screen for `/settings`. */
export const DEFAULT_SETTINGS_ROUTE = SETTINGS_SECTIONS[0].items[0].route

/** Build the route-name for a subsection (kept consistent across layout + router). */
export const settingsRouteName = (route: string) => `Settings.${route}`

/**
 * Flatten the config into nested child routes for the SettingsLayout parent.
 * `chromeless` items are excluded — they render outside the shell and are
 * registered as top-level routes (see router/index.ts) under the same route
 * name, so the sidebar's name-based navigation still resolves them.
 */
export function buildSettingsChildRoutes(): RouteRecordRaw[] {
  return SETTINGS_SECTIONS.flatMap((section) =>
    section.items
      .filter((item) => !item.chromeless && item.component)
      .map<RouteRecordRaw>((item) => ({
        path: item.route,
        name: settingsRouteName(item.route),
        component: item.component!,
        meta: {
          requiresAuth: true,
          settingsTitle: item.title,
          settingsEndpoint: item.endpoint,
          settingsDescription: item.description,
          settingsPlanned: item.planned ?? false,
          settingsRoles: item.roles,
          settingsPhase: item.phase,
        },
      })),
  )
}
